// ═══════════════════════════════════════════════════════════
// Worker API — Claude AI, email, SMS, HERE Maps, cameras
// Routed through /api/worker → ogemc-worker.ogemc.workers.dev
// ═══════════════════════════════════════════════════════════

// Always route through the local proxy — Vite proxies /api/worker → worker
// in dev (with changeOrigin), Pages Function does the same in production.
// Calling the worker URL directly in dev triggers CORS failures because
// browsers override the Origin header with the actual page origin (localhost).
const WORKER = '/api/worker';
const ORIGIN = import.meta.env.VITE_ORIGIN || 'https://apps.opengov.com';

const H = { 'Content-Type': 'application/json', Origin: ORIGIN };

// ── Claude AI ───────────────────────────────────────────

export interface ClaudeMessage {
  role: 'user' | 'assistant';
  content: string;
}

export async function callClaude(
  messages: ClaudeMessage[],
  opts?: { system?: string; maxTokens?: number; model?: string },
): Promise<string> {
  const res = await fetch(`${WORKER}/api/claude`, {
    method: 'POST',
    headers: H,
    body: JSON.stringify({
      model: opts?.model || 'claude-sonnet-4-6',
      max_tokens: opts?.maxTokens || 1024,
      system: opts?.system,
      messages,
    }),
  });
  if (!res.ok) throw new Error(`Claude API failed: ${res.status}`);
  const data = await res.json();
  return data.content?.[0]?.text || '';
}

// ── Email ───────────────────────────────────────────────

export async function sendEmail(to: string, subject: string, body: string): Promise<void> {
  const res = await fetch(`${WORKER}/api/email`, {
    method: 'POST', headers: H,
    body: JSON.stringify({ to, subject, body }),
  });
  if (!res.ok) throw new Error(`Email failed: ${res.status}`);
}

// ── SMS ─────────────────────────────────────────────────

export async function sendSMS(to: string, message: string): Promise<void> {
  const res = await fetch(`${WORKER}/api/sms`, {
    method: 'POST', headers: H,
    body: JSON.stringify({ to, message }),
  });
  if (!res.ok) throw new Error(`SMS failed: ${res.status}`);
}

// ── HERE Maps ───────────────────────────────────────────

export async function hereAutosuggest(query: string, at = '33.749,-84.388', limit = 5): Promise<any[]> {
  const res = await fetch(`${WORKER}/api/here/autosuggest?q=${encodeURIComponent(query)}&at=${at}&limit=${limit}`, { headers: H });
  if (!res.ok) return [];
  const data = await res.json();
  return data.items || [];
}

export async function hereGeocode(query: string): Promise<any> {
  const res = await fetch(`${WORKER}/api/here/geocode?q=${encodeURIComponent(query)}`, { headers: H });
  if (!res.ok) return null;
  const data = await res.json();
  return data.items?.[0] || null;
}

export async function hereReverseGeocode(lat: number, lng: number): Promise<any> {
  const res = await fetch(`${WORKER}/api/here/revgeocode?at=${lat},${lng}`, { headers: H });
  if (!res.ok) return null;
  const data = await res.json();
  return data.items?.[0] || null;
}

export async function hereBrowse(at: string, categories: string, limit = 100): Promise<any[]> {
  const res = await fetch(`${WORKER}/api/here/browse?at=${at}&categories=${categories}&limit=${limit}`, { headers: H });
  if (!res.ok) return [];
  const data = await res.json();
  return data.items || [];
}
